import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload as UploadIcon, Sparkles, AlertCircle, DollarSign, Image, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { API } from '../config/api';

const categories = [
  { value: 'photography', label: 'Photography' },
  { value: 'fine_art', label: 'Fine Art' },
  { value: 'film', label: 'Film' },
];

export default function Upload() {
  const navigate = useNavigate();
  const { isAuthenticated, isArtist, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    tags: '',
    image_url: '',
    preview_url: '',
    thumbnail_url: '',
    is_ai_generated: false,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }

  if (!isArtist && !isAdmin) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <AlertCircle className="mx-auto text-[#D4AF37] mb-6" size={48} />
          <h1 className="font-serif text-3xl text-[#F5F5F0] mb-4">Artist Account Required</h1>
          <p className="text-[#A1A1AA] mb-8">
            You need an artist account to upload artwork.
          </p>
          <Button onClick={() => navigate('/register')} className="btn-primary">
            Register as Artist
          </Button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - images and RAW formats
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/tiff',
      'image/heic', 'image/heif'
    ];
    const rawExtensions = ['.cr2', '.cr3', '.nef', '.arw', '.dng', '.raf', '.rw2', '.orf', '.pef', '.raw', '.tiff', '.tif', '.heic', '.heif'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const isRaw = rawExtensions.includes(ext);
    
    if (!allowedTypes.includes(file.type) && !isRaw) {
      toast.error('Please select an image file (JPG, PNG, GIF, WebP, HEIC, or RAW)');
      return;
    }

    // Validate file size (100MB max for RAW)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large (max 100MB)');
      return;
    }

    // Show preview (for viewable images only)
    if (allowedTypes.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      // For RAW files, show placeholder
      setImagePreview('raw');
    }

    // Upload file
    try {
      setUploading(true);
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      const previewUrl = response.data.preview_url || '';
      const thumbnailUrl = response.data.thumbnail_url || '';
      setFormData(prev => ({ 
        ...prev, 
        image_url: imageUrl, 
        preview_url: previewUrl, 
        thumbnail_url: thumbnailUrl 
      }));
      toast.success(response.data.is_raw ? 'RAW file uploaded!' : 'Image uploaded!');
    } catch {
      toast.error('Upload failed. Please try again.');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '', preview_url: '', thumbnail_url: '' }));
  };

  const generateDescription = async () => {
    if (!formData.title || !formData.category) {
      toast.error('Please enter a title and select a category first');
      return;
    }
    
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/generate-description`, null, {
        params: { title: formData.title, category: formData.category }
      });
      
      if (response.data.description) {
        setFormData(prev => ({ ...prev, description: response.data.description }));
        toast.success('Description generated');
      }
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.is_ai_generated) {
      toast.error('AI-generated art is not permitted on this platform');
      return;
    }

    if (!formData.title || !formData.description || !formData.category || !formData.price || !formData.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const artworkData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        image_url: formData.image_url,
        preview_url: formData.preview_url || null,
        thumbnail_url: formData.thumbnail_url || null,
        is_ai_generated: false,
      };

      // For admin, create directly
      if (isAdmin) {
        await axios.post(`${API}/artworks`, artworkData);
        toast.success('Artwork uploaded successfully');
        navigate('/gallery');
        return;
      }

      // For artists, initiate payment
      const response = await axios.post(`${API}/payments/listing-fee`, {
        origin_url: window.location.origin,
        artwork_data: artworkData
      });

      // Redirect to Stripe
      window.location.href = response.data.url;
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Share Your Work</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
            Upload Artwork
          </h1>
          {!isAdmin && (
            <div className="mt-4 p-4 bg-[#121212] border border-white/10 flex items-center gap-4">
              <DollarSign className="text-[#D4AF37] flex-shrink-0" size={24} />
              <div>
                <p className="text-[#F5F5F0]">$3.00 listing fee</p>
                <p className="text-[#71717A] text-sm">One-time fee per artwork. Featured for 30 days.</p>
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Image Upload */}
          <div className="form-group">
            <label className="form-label">Artwork Image *</label>
            
            {imagePreview ? (
              <div className="relative">
                {imagePreview === 'raw' ? (
                  <div className="w-full h-64 bg-[#121212] border border-white/10 flex flex-col items-center justify-center">
                    <Image className="text-[#D4AF37] mb-4" size={48} />
                    <p className="text-[#F5F5F0]">RAW File Uploaded</p>
                    <p className="text-[#71717A] text-sm mt-1">Preview not available for RAW files</p>
                  </div>
                ) : (
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full max-h-96 object-contain bg-[#121212] border border-white/10"
                  />
                )}
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-2 bg-black/80 text-white hover:bg-black transition-colors"
                >
                  <X size={18} />
                </button>
                {uploading && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                  </div>
                )}
              </div>
            ) : (
              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-white/20 hover:border-[#D4AF37]/50 transition-colors p-12 text-center">
                  <Image className="mx-auto text-[#71717A] mb-4" size={48} />
                  <p className="text-[#A1A1AA] mb-2">Click to upload or drag and drop</p>
                  <p className="text-[#71717A] text-sm">JPG, PNG, GIF, WebP, HEIC, or RAW files (max 100MB)</p>
                  <p className="text-[#71717A] text-xs mt-1">RAW: CR2, CR3, NEF, ARW, DNG, RAF, RW2, ORF, PEF, HEIC</p>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/tiff,image/heic,image/heif,.cr2,.cr3,.nef,.arw,.dng,.raf,.rw2,.orf,.pef,.raw,.tiff,.tif,.heic,.heif"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </label>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter artwork title"
              className="form-input"
              data-testid="title-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Category *</label>
            <Select value={formData.category} onValueChange={handleCategoryChange}>
              <SelectTrigger className="form-input" data-testid="category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value} className="text-[#F5F5F0]">
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="form-group">
            <div className="flex items-center justify-between mb-2">
              <label className="form-label mb-0">Description *</label>
              <Button
                type="button"
                onClick={generateDescription}
                disabled={generating}
                className="btn-secondary text-xs py-1 px-3 flex items-center gap-2"
                data-testid="generate-desc-btn"
              >
                <Sparkles size={14} />
                {generating ? 'Generating...' : 'AI Generate'}
              </Button>
            </div>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your artwork..."
              rows={4}
              className="form-input resize-none"
              data-testid="description-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Price (USD) *</label>
            <Input
              name="price"
              type="number"
              step="0.01"
              min="1"
              value={formData.price}
              onChange={handleChange}
              placeholder="0.00"
              className="form-input"
              data-testid="price-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Tags (comma separated)</label>
            <Input
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="landscape, nature, black and white"
              className="form-input"
              data-testid="tags-input"
            />
          </div>

          {/* AI Declaration */}
          <div className="p-4 bg-[#EF4444]/10 border border-[#EF4444]/30">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_ai_generated"
                checked={formData.is_ai_generated}
                onChange={handleChange}
                className="custom-checkbox mt-0.5"
                data-testid="ai-checkbox"
              />
              <div>
                <p className="text-[#EF4444] font-medium">Is this AI-generated art?</p>
                <p className="text-[#71717A] text-sm mt-1">
                  AI-generated art is not permitted on Mortal Works. 
                  Digital tools (Photoshop, Lightroom, etc.) are allowed.
                </p>
              </div>
            </label>
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={loading || uploading || formData.is_ai_generated || !formData.image_url}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              <UploadIcon size={18} />
              {loading ? 'Processing...' : (isAdmin ? 'Upload Artwork' : 'Pay $3 & Upload')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
